/**
 * TaskItem Component
 * Represents a single task with edit, toggle, and delete functionality
 * Supports drag-and-drop reordering
 */
import React from 'react';
import { motion } from 'framer-motion';
import { CheckSquare, Square, Trash2, Pencil, Check, Sun } from 'lucide-react';

export function TaskItem({
  task,
  quadrantId,
  isEditing,
  editingInput,
  onEditStart,
  onEditChange,
  onEditSave,
  onToggle,
  onDelete,
  onDragStart,
  onDragEnd,
  isDragging,
  onTogglePlanToday,
}) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`task-item ${task.completed ? 'completed' : ''} ${isDragging ? 'dragging' : ''}`}
      draggable="true"
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="task-content">
        <button
          className="task-checkbox"
          onClick={() => onToggle(quadrantId, task.id)}
          aria-label={task.completed ? `Mark task as incomplete: ${task.text}` : `Mark task as complete: ${task.text}`}
        >
          {task.completed ? (
            <CheckSquare size={16} className="task-icon" aria-hidden="true" />
          ) : (
            <Square size={16} className="task-icon" aria-hidden="true" />
          )}
        </button>
        {isEditing ? (
          <input
            type="text"
            value={editingInput !== undefined ? editingInput : task.text}
            onChange={(e) => onEditChange(e.target.value)}
            onBlur={() => onEditSave(quadrantId, task.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onEditSave(quadrantId, task.id);
              if (e.key === 'Escape') onEditStart(null);
            }}
            className="task-edit-input"
            aria-label="Edit task text"
            autoFocus
          />
        ) : (
          <span className="task-text-span">
            {task.text}
          </span>
        )}
      </div>
      <div className="task-actions">
        <button 
          className={`plan-today-btn ${task.plannedDate ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePlanToday && onTogglePlanToday(quadrantId, task.id);
          }}
          title={task.plannedDate ? "Đang trong kế hoạch hôm nay" : "Thêm vào kế hoạch hôm nay"}
          aria-label={task.plannedDate ? "Bỏ khỏi kế hoạch hôm nay" : "Thêm vào kế hoạch hôm nay"}
        >
          <Sun size={14} />
        </button>
        <button 
          className={`edit-task ${isEditing ? 'editing' : ''}`}
          onMouseDown={(e) => {
            if (isEditing) {
              e.preventDefault();
            }
          }}
          onClick={(e) => { 
            e.stopPropagation(); 
            if (isEditing) {
              onEditSave(quadrantId, task.id);
            } else {
              onEditStart({ quadrantId, taskId: task.id }); 
            }
          }}
          aria-label={isEditing ? "Save task edit" : `Edit task: ${task.text}`}
        >
          {isEditing ? <Check size={14} /> : <Pencil size={14} />}
        </button>
        <button 
          className="delete-task" 
          onClick={(e) => { e.stopPropagation(); onDelete(quadrantId, task.id); }}
          aria-label={`Delete task: ${task.text}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </motion.div>
  );
}
